import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/trpc/trpc";
import { OfferDialog, type OfferForEdit } from "@/components/offer-dialog";

export const Route = createFileRoute("/_authenticated/offers")({
  component: OffersPage,
});

function formatDiscount(o: OfferForEdit) {
  if (o.discountType === "percentage") return `-${o.discountValue}%`;
  if (o.discountType === "fixed") return `-${(o.discountValue / 100).toFixed(2)} zł`;
  return "Tylko etykieta";
}

function formatDateRange(o: OfferForEdit) {
  const from = o.validFrom ? new Date(o.validFrom).toLocaleDateString("pl-PL") : null;
  const until = o.validUntil ? new Date(o.validUntil).toLocaleDateString("pl-PL") : null;
  if (!from && !until) return "Bezterminowo";
  if (from && until) return `${from} – ${until}`;
  if (from) return `od ${from}`;
  return `do ${until}`;
}

function OffersPage() {
  const { data: offers, isLoading } = trpc.offer.list.useQuery();
  const utils = trpc.useUtils();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<OfferForEdit | null>(null);

  const toggleActive = trpc.offer.update.useMutation({
    onSuccess: () => utils.offer.list.invalidate(),
    onError: (err) => toast.error(err.message),
  });

  const deleteOffer = trpc.offer.delete.useMutation({
    onSuccess: () => {
      utils.offer.list.invalidate();
      toast.success("Oferta usunięta");
    },
    onError: (err) => toast.error(err.message),
  });

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Ładowanie...</p>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Oferty</h1>
          <p className="text-sm text-muted-foreground">
            {offers?.length ?? 0} ofert · Zniżki i wyróżnienia na wybrane zabiegi
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => {
            setEditing(null);
            setDialogOpen(true);
          }}
        >
          <Plus className="mr-1 size-4" />
          Dodaj ofertę
        </Button>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="px-4 py-3 font-medium">Tytuł</th>
                <th className="px-4 py-3 font-medium">Zniżka</th>
                <th className="px-4 py-3 font-medium">Zabiegi</th>
                <th className="px-4 py-3 font-medium">Okres</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {offers?.map((o) => (
                <tr key={o.id} className="border-b last:border-0">
                  <td className="px-4 py-3">
                    <div className="font-medium">{o.title}</div>
                    {o.description && (
                      <div className="text-xs text-muted-foreground line-clamp-1">
                        {o.description}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="outline">{formatDiscount(o as OfferForEdit)}</Badge>
                    {!o.stackableWithCoupons && (
                      <div className="text-xs text-muted-foreground mt-1">
                        bez kuponu
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1 max-w-xs">
                      {o.treatments.slice(0, 3).map((t) => (
                        <Badge key={t.id} variant="secondary" className="text-xs">
                          {t.name}
                        </Badge>
                      ))}
                      {o.treatments.length > 3 && (
                        <span className="text-xs text-muted-foreground self-center">
                          +{o.treatments.length - 3}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {formatDateRange(o as OfferForEdit)}
                  </td>
                  <td className="px-4 py-3">
                    <Badge
                      variant={o.isActive ? "default" : "secondary"}
                      className="cursor-pointer"
                      onClick={() =>
                        toggleActive.mutate({ id: o.id, isActive: !o.isActive })
                      }
                    >
                      {o.isActive ? "Aktywna" : "Nieaktywna"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditing(o as OfferForEdit);
                          setDialogOpen(true);
                        }}
                      >
                        Edytuj
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (confirm(`Usunąć ofertę "${o.title}"?`)) {
                            deleteOffer.mutate({ id: o.id });
                          }
                        }}
                      >
                        <Trash2 className="size-4 text-destructive" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {(offers?.length ?? 0) === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-8 text-center text-muted-foreground"
                  >
                    Brak ofert
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <OfferDialog open={dialogOpen} onOpenChange={setDialogOpen} offer={editing} />
    </div>
  );
}
